import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { producerCrmService, type MarketingEmailDraft } from '../services';

export type MarketingRecipient = {
  id: string;
  name: string;
  email: string;
  adminName: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  recipients: MarketingRecipient[];
};

const EMPTY_DRAFT: MarketingEmailDraft = {
  subject: '',
  preheader: '',
  headline: '',
  body: '',
  ctaLabel: '',
  ctaUrl: '',
};

const PRESETS: Array<{ id: string; label: string } & MarketingEmailDraft> = [
  {
    id: 'reativar',
    label: 'Quem parou',
    subject: '{{paroquia}}, o próximo evento pode sair ainda este mês',
    preheader: 'A página continua pronta. Falta só a data.',
    headline: 'Faz um tempo desde o último evento',
    body: `A {{paroquia}} já mostrou que sabe reunir gente. O último evento ficou para trás, e a inscrição do próximo continua a poucos passos.

Publique data, local e os ingressos. A gente cuida do restante.

Se travou em alguma etapa, responda este e-mail.`,
    ctaLabel: 'Abrir o painel',
    ctaUrl: 'https://meusacramento.com/login',
  },
  {
    id: 'primeiro',
    label: 'Primeiro evento',
    subject: '{{paroquia}}, seu primeiro evento pode ficar no ar hoje',
    preheader: 'O cadastro está feito. Falta publicar.',
    headline: 'Seu primeiro evento ainda não foi para o ar',
    body: `A {{paroquia}} já está na plataforma. O que falta é o primeiro evento publicado: um título, a data e como as pessoas se inscrevem.

Se quiser, responda este e-mail e montamos o anúncio juntos.`,
    ctaLabel: 'Criar o primeiro evento',
    ctaUrl: 'https://meusacramento.com/login',
  },
  {
    id: 'branco',
    label: 'Em branco',
    ...EMPTY_DRAFT,
  },
];

function firstName(value: string): string {
  return value.trim().split(/\s+/)[0] || '';
}

export default function ProducerMarketingEmailDialog({ open, onClose, recipients }: Props) {
  const [presetId, setPresetId] = useState(PRESETS[0].id);
  const [draft, setDraft] = useState<MarketingEmailDraft>(PRESETS[0]);
  const [aiPrompt, setAiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [resultError, setResultError] = useState<string | null>(null);

  const sample = recipients[0];
  const sampleNome = firstName(sample?.adminName || '') || 'João';
  const sampleParoquia = sample?.name || 'Paróquia São José';

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setResultError(null);
    setConfirmSend(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setPreviewing(true);
      try {
        const response = await producerCrmService.previewMarketingEmail({
          ...draft,
          sampleNome,
          sampleParoquia,
        });
        if (cancelled) return;
        setPreviewHtml(response.data.html);
        setPreviewSubject(response.data.subject);
        setDraftError(response.data.valid ? null : response.data.message);
      } catch {
        if (!cancelled) setDraftError('Não foi possível atualizar o preview.');
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, draft, sampleNome, sampleParoquia]);

  const patch = (partial: Partial<MarketingEmailDraft>) => {
    setDraft((current) => ({ ...current, ...partial }));
    setResult(null);
    setResultError(null);
  };

  const generateWithAi = async () => {
    const prompt = aiPrompt.trim();
    if (prompt.length < 8) {
      setResultError('Descreva o e-mail em pelo menos uma frase.');
      return;
    }
    setGenerating(true);
    setResult(null);
    setResultError(null);
    try {
      const response = await producerCrmService.generateMarketingEmail(prompt);
      setPresetId('ia');
      patch(response.data);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível gerar o e-mail.';
      setResultError(message);
    } finally {
      setGenerating(false);
    }
  };

  const send = async (mode: 'test' | 'producers') => {
    setSending(true);
    setResult(null);
    setResultError(null);
    try {
      const response = await producerCrmService.sendMarketingEmail({
        ...draft,
        mode,
        parishIds: recipients.map((item) => item.id),
        sampleNome,
        sampleParoquia,
      });
      const data = response.data;
      if (mode === 'test') {
        setResult(`Teste enviado para ${data.testTo}.`);
      } else {
        const failNote = data.failed ? ` ${data.failed} falharam.` : '';
        const skipNote = data.skippedNoEmail ? ` ${data.skippedNoEmail} sem e-mail.` : '';
        setResult(`Enviado para ${data.sent} produtor${data.sent === 1 ? '' : 'es'}.${failNote}${skipNote}`);
      }
      setConfirmSend(false);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível enviar.';
      setResultError(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>E-mail marketing</DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              size="small"
              label="Pedido para a IA"
              value={aiPrompt}
              onChange={(event) => setAiPrompt(event.target.value)}
              multiline
              minRows={3}
              placeholder="Ex.: e-mail para quem não publica há 6 meses, lembrando que o painel continua pronto e convidando a criar o próximo retiro."
            />
            <Button variant="outlined" onClick={generateWithAi} disabled={generating || sending}>
              {generating ? 'Gerando o e-mail…' : 'Gerar e-mail com IA'}
            </Button>
            <TextField
              select
              size="small"
              label="Modelo"
              value={presetId}
              onChange={(event) => {
                const nextId = event.target.value;
                setPresetId(nextId);
                const preset = PRESETS.find((item) => item.id === nextId);
                if (!preset) return;
                patch({
                  subject: preset.subject,
                  preheader: preset.preheader,
                  headline: preset.headline,
                  body: preset.body,
                  ctaLabel: preset.ctaLabel,
                  ctaUrl: preset.ctaUrl,
                });
              }}
            >
              {PRESETS.map((preset) => (
                <MenuItem key={preset.id} value={preset.id}>
                  {preset.label}
                </MenuItem>
              ))}
              <MenuItem value="ia">Gerado pela IA</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Assunto"
              value={draft.subject}
              onChange={(event) => patch({ subject: event.target.value })}
              helperText={`${draft.subject.length} caracteres · o ideal fica perto de 50`}
            />
            <TextField
              size="small"
              label="Texto de preview da caixa de entrada"
              value={draft.preheader}
              onChange={(event) => patch({ preheader: event.target.value })}
            />
            <TextField
              size="small"
              label="Título dentro do e-mail"
              value={draft.headline}
              onChange={(event) => patch({ headline: event.target.value })}
            />
            <TextField
              size="small"
              label="Mensagem (HTML)"
              value={draft.body}
              onChange={(event) => patch({ body: event.target.value })}
              multiline
              minRows={12}
              InputProps={{ sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 } }}
              helperText="Pode colar HTML: tags, tabela, imagem e estilo. Um documento inteiro (com <html>) substitui o layout. Sem tag, o texto vira parágrafos. {{nome}} e {{paroquia}} continuam valendo."
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                size="small"
                label="Texto do botão"
                value={draft.ctaLabel}
                onChange={(event) => patch({ ctaLabel: event.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label="Link do botão"
                value={draft.ctaUrl}
                onChange={(event) => patch({ ctaUrl: event.target.value })}
                fullWidth
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {recipients.length} produtor{recipients.length === 1 ? '' : 'es'} com e-mail nesta lista.
              O preview usa {sampleNome} · {sampleParoquia}.
            </Typography>
            {draftError ? <Alert severity="warning">{draftError}</Alert> : null}
            {recipients.length > 200 ? (
              <Alert severity="warning">Cada envio leva no máximo 200 produtores. Marque um grupo menor.</Alert>
            ) : null}
            {result ? <Alert severity="success">{result}</Alert> : null}
            {resultError ? <Alert severity="error">{resultError}</Alert> : null}
            {confirmSend ? (
              <Alert
                severity="info"
                action={
                  <Button color="inherit" size="small" disabled={sending} onClick={() => send('producers')}>
                    Confirmar
                  </Button>
                }
              >
                Enviar agora para {recipients.length} produtor{recipients.length === 1 ? '' : 'es'}?
              </Alert>
            ) : null}
          </Stack>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {previewing ? 'Atualizando preview…' : 'Preview'}
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#f8fafc',
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  De: Meu Sacramento
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {previewSubject || 'Assunto do e-mail'}
                </Typography>
              </Box>
              <Box
                component="iframe"
                title="Preview do e-mail"
                sandbox=""
                srcDoc={previewHtml || '<p style="font-family:Arial;padding:24px;color:#64748b;">O preview aparece aqui.</p>'}
                sx={{ width: '100%', height: 560, border: 0, bgcolor: '#eef3f8' }}
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={sending || generating}>
          Fechar
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" disabled={sending || generating || !!draftError} onClick={() => send('test')}>
          Enviar teste para mim
        </Button>
        <Button
          variant="contained"
          disabled={sending || generating || !!draftError || recipients.length === 0 || recipients.length > 200}
          onClick={() => setConfirmSend(true)}
        >
          Enviar para a lista
        </Button>
      </DialogActions>
    </Dialog>
  );
}
